import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, timeout } from 'rxjs/operators';
import { PageAccessService } from './page-access.service';
import { PageId } from './page-id.enum';
import { Capacitor } from '@capacitor/core';
import { GetStartedService } from '../get-started/get-started.service';
import { AccountGroupService } from '../account-group/account-group.service';
import { AppConfigService } from '../app-config.service';
import { AuthService } from '../auth.service';

const hasVisitedGetStartedKey = `has-visited-getting-started-page`;

@Injectable({ providedIn: 'root' })
export class PageAccessGuard {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly pageVisibilityService = inject(PageAccessService);
  private readonly getStartedService = inject(GetStartedService);
  private readonly accountGroupService = inject(AccountGroupService);
  private readonly appConfigService = inject(AppConfigService);

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    return this.canActivate(childRoute);
  }

  canActivate(
    route: ActivatedRouteSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    let pageId: PageId = route.data['pageId'];
    if (!pageId) {
      return true;
    }

    // TODO: remove once legacy notifications are accessible on mobile
    if (Capacitor.isNativePlatform() && pageId === PageId.new_notification_settings) {
      pageId = PageId.profile_settings;
    }

    if (!this.authService.userId() || localStorage.getItem(hasVisitedGetStartedKey) === 'true') {
      return this.isPageAccessible(pageId);
    }
    localStorage.setItem(hasVisitedGetStartedKey, 'true');

    return combineLatest([
      this.getStartedService.getTrackerHistory(),
      this.appConfigService.legacyWhitelabelConfig$,
    ]).pipe(
      switchMap(([trackerHistory, config]) => {
        if (
          trackerHistory === null &&
          config.businessCenterConfiguration?.showGetStarted &&
          config.businessCenterConfiguration?.showDashboard
        ) {
          this.getStartedService.setTracker('');
          return of(
            this.router.createUrlTree([
              `/account/location/${this.accountGroupService.currentAccountGroupId()}/home/get-started`,
            ]),
          );
        }
        return this.isPageAccessible(pageId);
      }),
    );
  }

  isPageAccessible(pageId: PageId): Observable<boolean | UrlTree> {
    return this.pageVisibilityService.isPageAccessible$(pageId).pipe(
      switchMap((accessGranted) => {
        if (accessGranted) {
          return of(true);
        }
        console.log('User is not allowed access to requested page, redirecting...', pageId);

        return this.pageVisibilityService
          .firstAccessiblePageUrl$()
          .pipe(map((url) => this.router.createUrlTree([url])));
      }),
      timeout(10000),
      catchError(async () => this.router.createUrlTree(['account/select-location'])),
      take(1),
    );
  }
}
